/**
 * SubmittalDrawer Component
 * Create / Edit / View Submittal form
 * BuilderBel Construction Project Management System
 *
 * 90% width drawer (complex multi-section form)
 */

import {
  X,
  FileText,
  Calendar,
  MapPin,
  User,
  ChevronDown,
  ChevronRight,
  ClipboardList,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useCreateSubmittalMutation,
  useUpdateSubmittalMutation,
} from '../../services/submittalApi';
import type {
  CreateSubmittalRequest,
  Submittal,
  SubmittalPriority,
  SubmittalType,
  UpdateSubmittalRequest,
} from '../../types/submittal';
import { DatePicker, Dropdown, Input, NumberInput, SearchableDropdown, TextArea } from '../forms';

// ============================================================================
// Constants
// ============================================================================

const TYPE_OPTIONS = [
  { value: 'shop_drawing', label: 'Shop Drawing' },
  { value: 'product_data', label: 'Product Data' },
  { value: 'sample', label: 'Sample' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'mock_up', label: 'Mock Up' },
  { value: 'test_report', label: 'Test Report' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// ============================================================================
// Props
// ============================================================================

export interface SubmittalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  submittal?: Submittal | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// ============================================================================
// Form State
// ============================================================================

interface FormState {
  title: string;
  description: string;
  type: SubmittalType | '';
  priority: SubmittalPriority;
  // Spec reference
  specDivision: string;
  specSection: string;
  specTitle: string;
  specParagraph: string;
  // Assignment
  ballInCourt: string;
  ballInCourtName: string;
  contractorId: string;
  contractorName: string;
  // Dates
  dueDate: string;
  requiredOnSite: string;
  leadTimeDays: number | '';
  // Notes
  notes: string;
  internalNotes: string;
  // Location
  location: string;
  areaOfWork: string;
}

const defaultFormState: FormState = {
  title: '',
  description: '',
  type: '',
  priority: 'medium',
  specDivision: '',
  specSection: '',
  specTitle: '',
  specParagraph: '',
  ballInCourt: '',
  ballInCourtName: '',
  contractorId: '',
  contractorName: '',
  dueDate: '',
  requiredOnSite: '',
  leadTimeDays: '',
  notes: '',
  internalNotes: '',
  location: '',
  areaOfWork: '',
};

// ============================================================================
// Section collapse state
// ============================================================================

interface SectionState {
  basicInfo: boolean;
  specReference: boolean;
  assignment: boolean;
  dates: boolean;
  notes: boolean;
  reviewChain: boolean;
}

// ============================================================================
// Helper: extract string ID
// ============================================================================

function extractId(value: string | { _id: string } | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value._id;
}

function extractName(
  value: string | { _id: string; firstName: string; lastName: string } | undefined
): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return `${value.firstName} ${value.lastName}`;
}

// ============================================================================
// Component
// ============================================================================

const SubmittalDrawer: React.FC<SubmittalDrawerProps> = ({
  isOpen,
  onClose,
  submittal,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [createSubmittal, { isLoading: isCreating }] = useCreateSubmittalMutation();
  const [updateSubmittal, { isLoading: isUpdating }] = useUpdateSubmittalMutation();

  const isSubmitting = isCreating || isUpdating;
  const isViewMode = mode === 'view';

  // Form state
  const [form, setForm] = useState<FormState>(defaultFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Section collapse state — all open by default
  const [sections, setSections] = useState<SectionState>({
    basicInfo: true,
    specReference: true,
    assignment: true,
    dates: true,
    notes: false,
    reviewChain: false,
  });

  // ============================================================================
  // Populate form when editing / viewing
  // ============================================================================

  useEffect(() => {
    if (submittal && (mode === 'edit' || mode === 'view')) {
      const firstSpec = submittal.specSections?.[0];
      setForm({
        title: submittal.title || '',
        description: submittal.description || '',
        type: submittal.type || '',
        priority: submittal.priority || 'medium',
        specDivision: firstSpec?.division || '',
        specSection: firstSpec?.section || '',
        specTitle: firstSpec?.title || '',
        specParagraph: firstSpec?.paragraph || '',
        ballInCourt: extractId(submittal.ballInCourt as string | { _id: string }),
        ballInCourtName:
          submittal.ballInCourtName ||
          extractName(
            submittal.ballInCourt as string | { _id: string; firstName: string; lastName: string }
          ),
        contractorId: submittal.contractorId || '',
        contractorName: submittal.contractorName || '',
        dueDate: submittal.dueDate ? submittal.dueDate.split('T')[0] : '',
        requiredOnSite: submittal.requiredOnSite ? submittal.requiredOnSite.split('T')[0] : '',
        leadTimeDays: submittal.leadTimeDays ?? '',
        notes: submittal.notes || '',
        internalNotes: submittal.internalNotes || '',
        location: submittal.location || '',
        areaOfWork: submittal.areaOfWork || '',
      });
    } else if (mode === 'create') {
      setForm(defaultFormState);
      setErrors({});
    }
  }, [submittal, mode, isOpen]);

  // ============================================================================
  // Field change handler
  // ============================================================================

  const handleChange = (field: keyof FormState, value: string | number | '') => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // ============================================================================
  // Validation
  // ============================================================================

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.title.trim()) newErrors.title = 'Title is required';
    if (!form.type) newErrors.type = 'Type is required';
    if (!form.dueDate) newErrors.dueDate = 'Due date is required';
    if (!form.ballInCourt) newErrors.ballInCourt = 'Ball in court is required';
    if (!form.ballInCourtName.trim()) newErrors.ballInCourtName = 'Ball in court name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // Submit
  // ============================================================================

  const handleSubmit = async () => {
    if (!validate() || !projectId) return;

    const specSections =
      form.specDivision && form.specSection && form.specTitle
        ? [
            {
              division: form.specDivision,
              section: form.specSection,
              title: form.specTitle,
              paragraph: form.specParagraph || undefined,
            },
          ]
        : [];

    try {
      if (mode === 'create') {
        const payload: CreateSubmittalRequest = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          type: form.type as SubmittalType,
          priority: form.priority,
          specSections,
          ballInCourt: form.ballInCourt,
          ballInCourtName: form.ballInCourtName.trim(),
          dueDate: form.dueDate,
          requiredOnSite: form.requiredOnSite || undefined,
          leadTimeDays: form.leadTimeDays !== '' ? Number(form.leadTimeDays) : undefined,
          contractorId: form.contractorId || undefined,
          contractorName: form.contractorName.trim() || undefined,
          notes: form.notes.trim() || undefined,
          internalNotes: form.internalNotes.trim() || undefined,
          location: form.location.trim() || undefined,
          areaOfWork: form.areaOfWork.trim() || undefined,
        };

        await createSubmittal({ projectId, data: payload }).unwrap();
        toast.success('Submittal created successfully');
      } else if (mode === 'edit' && submittal) {
        const payload: UpdateSubmittalRequest = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          type: form.type as SubmittalType,
          priority: form.priority,
          specSections,
          dueDate: form.dueDate,
          requiredOnSite: form.requiredOnSite || undefined,
          leadTimeDays: form.leadTimeDays !== '' ? Number(form.leadTimeDays) : undefined,
          contractorId: form.contractorId || undefined,
          contractorName: form.contractorName.trim() || undefined,
          notes: form.notes.trim() || undefined,
          internalNotes: form.internalNotes.trim() || undefined,
          location: form.location.trim() || undefined,
          areaOfWork: form.areaOfWork.trim() || undefined,
        };

        await updateSubmittal({
          projectId,
          submittalId: submittal._id,
          data: payload,
        }).unwrap();
        toast.success('Submittal updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch (_err) {
      toast.error('Failed to save submittal. Please try again.');
    }
  };

  // ============================================================================
  // Section toggle
  // ============================================================================

  const toggleSection = (key: keyof SectionState) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ============================================================================
  // Section header component
  // ============================================================================

  const SectionHeader: React.FC<{
    title: string;
    icon: React.ReactNode;
    sectionKey: keyof SectionState;
  }> = ({ title, icon, sectionKey }) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full py-3 border-b border-gray-200 mb-4 group"
    >
      <div className="flex items-center space-x-2">
        <span className="text-gray-400 group-hover:text-brand-600">{icon}</span>
        <span className="text-sm font-semibold text-gray-700 group-hover:text-brand-700">
          {title}
        </span>
      </div>
      {sections[sectionKey] ? (
        <ChevronDown className="h-4 w-4 text-gray-400" />
      ) : (
        <ChevronRight className="h-4 w-4 text-gray-400" />
      )}
    </button>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen) return null;

  const drawerTitle =
    mode === 'create'
      ? 'New Submittal'
      : mode === 'edit'
        ? `Edit Submittal${submittal?.submittalNumber ? ` — ${submittal.submittalNumber}` : ''}`
        : `Submittal — ${submittal?.submittalNumber ?? ''}`;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer — 90% width for complex form */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] inset-6 animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <ClipboardList className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{drawerTitle}</h2>
                {submittal && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {submittal.type?.replace(/_/g, ' ')} — Rev {submittal.currentRevision}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* ── Basic Info ── */}
              <div>
                <SectionHeader
                  title="Basic Information"
                  icon={<FileText className="h-4 w-4" />}
                  sectionKey="basicInfo"
                />
                {sections.basicInfo && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Title — full width */}
                    <div className="col-span-2">
                      <Input
                        label="Title *"
                        value={form.title}
                        onChange={e => handleChange('title', e.target.value)}
                        placeholder="Submittal title"
                        disabled={isViewMode}
                        error={errors.title}
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Type *
                      </label>
                      <Dropdown
                        value={form.type}
                        onChange={value => handleChange('type', value)}
                        options={TYPE_OPTIONS}
                        placeholder="Select type"
                        disabled={isViewMode}
                        error={errors.type}
                      />
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Priority
                      </label>
                      <Dropdown
                        value={form.priority}
                        onChange={value => handleChange('priority', value)}
                        options={PRIORITY_OPTIONS}
                        placeholder="Select priority"
                        disabled={isViewMode}
                      />
                    </div>

                    {/* Description — full width */}
                    <div className="col-span-2">
                      <TextArea
                        label="Description"
                        value={form.description}
                        onChange={value => handleChange('description', value)}
                        placeholder="Brief description of the submittal"
                        rows={3}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Spec Reference ── */}
              <div>
                <SectionHeader
                  title="Spec Reference"
                  icon={<FileText className="h-4 w-4" />}
                  sectionKey="specReference"
                />
                {sections.specReference && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        label="Division"
                        value={form.specDivision}
                        onChange={e => handleChange('specDivision', e.target.value)}
                        placeholder="e.g. 03"
                        maxLength={10}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <Input
                        label="Section"
                        value={form.specSection}
                        onChange={e => handleChange('specSection', e.target.value)}
                        placeholder="e.g. 03 30 00"
                        maxLength={20}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <Input
                        label="Spec Title"
                        value={form.specTitle}
                        onChange={e => handleChange('specTitle', e.target.value)}
                        placeholder="e.g. Cast-in-Place Concrete"
                        maxLength={255}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <Input
                        label="Paragraph"
                        value={form.specParagraph}
                        onChange={e => handleChange('specParagraph', e.target.value)}
                        placeholder="e.g. 2.01"
                        maxLength={50}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Assignment ── */}
              <div>
                <SectionHeader
                  title="Assignment"
                  icon={<User className="h-4 w-4" />}
                  sectionKey="assignment"
                />
                {sections.assignment && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Ball in court name (free text — team lookup could be wired here) */}
                    <div>
                      <Input
                        label="Ball In Court (User ID) *"
                        value={form.ballInCourt}
                        onChange={e => handleChange('ballInCourt', e.target.value)}
                        placeholder="MongoDB user ID"
                        disabled={isViewMode}
                        error={errors.ballInCourt}
                      />
                    </div>
                    <div>
                      <Input
                        label="Ball In Court Name *"
                        value={form.ballInCourtName}
                        onChange={e => handleChange('ballInCourtName', e.target.value)}
                        placeholder="Full name"
                        disabled={isViewMode}
                        error={errors.ballInCourtName}
                      />
                    </div>
                    <div>
                      <Input
                        label="Contractor Name"
                        value={form.contractorName}
                        onChange={e => handleChange('contractorName', e.target.value)}
                        placeholder="Contractor / subcontractor"
                        maxLength={200}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <Input
                        label="Contractor ID"
                        value={form.contractorId}
                        onChange={e => handleChange('contractorId', e.target.value)}
                        placeholder="Optional vendor ID"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Dates ── */}
              <div>
                <SectionHeader
                  title="Dates"
                  icon={<Calendar className="h-4 w-4" />}
                  sectionKey="dates"
                />
                {sections.dates && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Due Date *
                      </label>
                      <DatePicker
                        value={form.dueDate}
                        onChange={value => handleChange('dueDate', value)}
                        disabled={isViewMode}
                        error={errors.dueDate}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Required On Site
                      </label>
                      <DatePicker
                        value={form.requiredOnSite}
                        onChange={value => handleChange('requiredOnSite', value)}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <NumberInput
                        label="Lead Time (days)"
                        value={form.leadTimeDays === '' ? undefined : Number(form.leadTimeDays)}
                        onChange={value => handleChange('leadTimeDays', value ?? '')}
                        min={0}
                        placeholder="0"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Location ── */}
              <div>
                <SectionHeader
                  title="Location"
                  icon={<MapPin className="h-4 w-4" />}
                  sectionKey="notes"
                />
                {sections.notes && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        label="Location"
                        value={form.location}
                        onChange={e => handleChange('location', e.target.value)}
                        placeholder="Location description"
                        maxLength={500}
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <Input
                        label="Area of Work"
                        value={form.areaOfWork}
                        onChange={e => handleChange('areaOfWork', e.target.value)}
                        placeholder="Area description"
                        maxLength={200}
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="col-span-2">
                      <TextArea
                        label="Notes"
                        value={form.notes}
                        onChange={value => handleChange('notes', value)}
                        placeholder="Public notes visible to all parties"
                        rows={3}
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="col-span-2">
                      <TextArea
                        label="Internal Notes"
                        value={form.internalNotes}
                        onChange={value => handleChange('internalNotes', value)}
                        placeholder="Internal notes (not shared externally)"
                        rows={3}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Review Chain (view only) ── */}
              {submittal && submittal.reviewers.length > 0 && (
                <div>
                  <SectionHeader
                    title="Review Chain"
                    icon={<ClipboardList className="h-4 w-4" />}
                    sectionKey="reviewChain"
                  />
                  {sections.reviewChain && (
                    <div className="space-y-2">
                      {submittal.reviewers.map((reviewer, index) => (
                        <div
                          key={reviewer._id ?? index}
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center text-xs font-medium text-brand-700">
                              {reviewer.order}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {reviewer.userName}
                              </p>
                              <p className="text-xs text-gray-500">{reviewer.role}</p>
                            </div>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              reviewer.status === 'approved'
                                ? 'bg-green-100 text-green-700'
                                : reviewer.status === 'rejected'
                                  ? 'bg-red-100 text-red-700'
                                  : reviewer.status === 'in_progress'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {reviewer.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {!isViewMode && (
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 flex items-center justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isSubmitting && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
                <span>{mode === 'create' ? 'Create Submittal' : 'Save Changes'}</span>
              </button>
            </div>
          )}

          {isViewMode && (
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 flex justify-end rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default SubmittalDrawer;
